import { useUser } from '@/lib/hooks';
import { Profile } from '@/lib/models/client.types';
import { profileService } from '@/lib/services';
import { userState } from '@/lib/store/user.store';
import { logger } from '@/lib/utils/logger';
import { observable } from '@legendapp/state';
import { Session } from 'next-auth';

// Types
export interface ProfileState {
  profile: {
    data: Profile | null;
    isLoading: boolean;
    error: Error | null;
  };
  session: Session | null;
}

// Initial state
const initialState: ProfileState = {
  profile: {
    data: null,
    isLoading: false,
    error: null,
  },
  session: null,
};

// Create the observable store
export const profileState = observable<ProfileState>(initialState);

// Action creators
export const profileActions = {
  // Session management
  setSession: (session: Session | null) => {
    logger.debug('[profileStore] Setting session', { session });
    profileState.session.set(session);
  },

  // Profile actions
  setProfile: (profile: Profile | null) => {
    logger.debug('[profileStore] Setting profile', { profile });
    profileState.profile.data.set(profile);
  },

  startLoading: () => {
    logger.debug('[profileStore] Starting loading');
    profileState.profile.isLoading.set(true);
    profileState.profile.error.set(null);
  },

  setError: (error: Error) => {
    logger.debug('[profileStore] Setting error', { error });
    profileState.profile.isLoading.set(false);
    profileState.profile.error.set(error);
  },

  completeLoading: () => {
    logger.debug('[profileStore] Completing loading');
    profileState.profile.isLoading.set(false);
  },

  reset: () => {
    logger.debug('[profileStore] Resetting state');
    profileState.set(initialState);
  },

  // Service-connected actions
  fetchProfile: async () => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    try {
      profileActions.startLoading();
      try {
        const profile = await profileService.getProfile(
          session.user.id,
          session
        );
        profileActions.setProfile(profile);
      } catch (err) {
        // If fetching fails, try creating a profile
        try {
          await profileActions.createProfile();
        } catch (createError) {
          // Only log error if creating also fails
          logger.error(
            '[profileStore] Failed to fetch and create profile:',
            createError + ' ' + err
          );
          profileActions.setError(createError as Error);
        }
      }
    } finally {
      profileActions.completeLoading();
    }
  },

  createProfile: async () => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    const role = userState.profile.data.role.get();

    if (role === 'user') return;

    try {
      profileActions.startLoading();
      const newProfile = await profileService.createProfile(
        session.user.id,
        session
      );
      profileActions.setProfile(newProfile);
    } catch (error) {
      logger.error('[profileStore] Failed to create profile:', error);
      profileActions.setError(error as Error);
    } finally {
      profileActions.completeLoading();
    }
  },

  updateProfile: async (data: Partial<Profile>) => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    try {
      profileActions.startLoading();
      const updatedProfile = await profileService.updateProfile(
        session.user.id,
        data,
        session
      );
      profileActions.setProfile(updatedProfile);
    } catch (error) {
      logger.error('[profileStore] Failed to update profile:', error);
      profileActions.setError(error as Error);
    } finally {
      profileActions.completeLoading();
    }
  },

  updateLanguage: async (language: string) => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    try {
      profileActions.startLoading();
      const updatedProfile = await profileService.updateLanguage(
        session.user.id,
        language,
        session
      );
      profileActions.setProfile(updatedProfile);
    } catch (error) {
      logger.error('[profileStore] Failed to update language:', error);
      profileActions.setError(error as Error);
    } finally {
      profileActions.completeLoading();
    }
  },
};

// Selectors
export const profileSelectors = {
  getProfile: () => profileState.profile.data.get(),
  isLoading: () => profileState.profile.isLoading.get(),
  getError: () => profileState.profile.error.get(),
  getSession: () => profileState.session.get(),
};
